/**
 * BotTakingAwhile
 * Renders a bot message letting the user know it's taking awhile, but the bot is still working on their request.
 *
 * - Injects the prebuilt activity from quickMessageConfig.waitingLongerThanUsual
 * - Forwards all BotMessage props except 'activity'; defaults disableButtons to false
 * - Emits a "Loading Delay" analytics event on mount
 *
 * Intended for use when a 'userWaiting' event appears in the transcript.
 */
import React, { useEffect, useRef } from 'react';
import { Activity } from '../../types';
import { craftCustomActivityMessage } from '../../messageUtils';
import quickMessageConfig from '../../resources/quickMessageConfig.json';
import { BotMessage } from '../../jsxElements';
import { logEvent as logAnalyticsEvent } from '../../analytics';
import type { BotMessageProps } from '../../jsxElements/BotMessage';
import { TIME_SECONDS_LONGER_WAIT_THAN_USUAL } from '../../constants';

export const getUserWaitingLongerThanUsualMessage = (): Activity => {
    const thinkingActivity: Activity = craftCustomActivityMessage(
        quickMessageConfig.waitingLongerThanUsual
    );
    return thinkingActivity;
};

type BotTakingAwhileProps = Omit<BotMessageProps, 'activity'>;

export const BotTakingAwhile: React.FC<BotTakingAwhileProps> = (props) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Fire analytics & scroll into view on mount
    useEffect(() => {
        logAnalyticsEvent('Loading Delay', {
            delay_time: `${TIME_SECONDS_LONGER_WAIT_THAN_USUAL}s`,
        });

        containerRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    // Merge forwarded props with the component-specific activity
    const mergedProps: BotMessageProps = {
        ...props,
        activity: getUserWaitingLongerThanUsualMessage(),
        disableButtons: props.disableButtons ?? false,
    };

    return (
        <div ref={containerRef}>
            <BotMessage {...mergedProps} />
        </div>
    );
};
